import { PlaybookChapter, MathData, KvListData } from '@/types/gamePlan';

// Office-friendly KPI worksheet: the owner fills in THEIR numbers; our HQ
// values sit beside each field as subtle hints/nudges, not prescriptions.
// Standalone brand-styled HTML, printed from a new window.

const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export function printKpis(chapters: PlaybookChapter[]): void {
  const kpi = chapters.find((c) => c.id === 'kpi');
  if (!kpi) return;
  const sec = (title: string) => kpi.sections.find((s) => s.title === title);

  const mathFrag = sec('The Math')?.fragments[0];
  const math = mathFrag ? (mathFrag.data as unknown as MathData) : undefined;

  const channels = (sec('Weekly Lead Target by Channel')?.fragments ?? []).map((f) => {
    const d = f.data as { label?: string; metric?: string };
    return { name: d.label ?? f.label, hint: d.metric ?? '' };
  });

  const phases = (sec('Cumulative Targets by Phase')?.fragments ?? []).map((f) => {
    const d = f.data as unknown as KvListData & { label?: string };
    const row = (l: string) => d.rows?.find((r) => r.label === l)?.value ?? '';
    return { range: (d as { label?: string }).label ?? f.label, goal: row('Goal'), leads: row('Leads') };
  });

  // Blank the owner writes in; `hint` is our subtle HQ nudge underneath.
  const fill = (hint: string) =>
    `<div class="fill"></div>${hint ? `<div class="hint">HQ: ${esc(hint)}</div>` : ''}`;

  const mathHtml = math
    ? `<div class="math">
         <div class="mbox"><div class="ml">Member Goal</div>${fill(String(math.goal))}</div>
         <div class="op">÷</div>
         <div class="mbox"><div class="ml">Assumed Conversion</div>${fill(math.conversion)}</div>
         <div class="op">=</div>
         <div class="mbox"><div class="ml">Leads Needed</div><div class="fill"></div><div class="hint">Goal ÷ Conversion · HQ: ${esc(
           String(math.leads),
         )}</div></div>
       </div>
       <p class="pace">Your channel targets below should add up to about your weekly pace — <b>HQ suggests ${esc(
         math.pace,
       )}</b> across the 16-week runway.</p>`
    : '';

  const channelRows = channels
    .map(
      (c) =>
        `<tr><td class="name">${esc(c.name)}</td><td class="hintcell">${esc(
          c.hint,
        )}</td><td class="fillcell"></td><td class="fillcell"></td></tr>`,
    )
    .join('');
  const totalRow = `<tr class="total"><td class="name">Weekly total</td><td class="hintcell">≈ ${esc(
    math ? math.pace.replace(/[^0-9]/g, '') || '13' : '13',
  )}</td><td class="fillcell"></td><td class="fillcell"></td></tr>`;

  const phaseRows = phases
    .map(
      (p) =>
        `<tr><td class="name">${esc(p.range)}</td><td class="hintcell">${esc(
          p.goal,
        )} members · ${esc(p.leads)} leads</td><td class="fillcell"></td></tr>`,
    )
    .join('');

  const html = `<!doctype html><html><head><meta charset="utf-8">
<title>Tee Box — Targets Worksheet</title>
<style>
  @page { margin: 14mm; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { margin: 0; font-family: ui-sans-serif, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #16241E; background: #FBF6EC; }
  .sheet { max-width: 760px; margin: 0 auto; padding: 8px; }
  .hdr { background: #0C3D2D; color: #FBF6EC; border-radius: 12px; padding: 22px 26px; }
  .wm { font-weight: 800; letter-spacing: .18em; font-size: 12px; opacity: .8; }
  .eyebrow { font-size: 11px; letter-spacing: .22em; text-transform: uppercase; color: #E2D1A7; margin-top: 10px; }
  .hdr h1 { margin: 4px 0 0; font-size: 30px; font-weight: 800; }
  .rule { height: 3px; width: 54px; background: #B58F2D; border-radius: 3px; margin: 12px 0 10px; }
  .sub { font-size: 13px; color: rgba(251,246,236,.82); }
  .math { display: flex; align-items: flex-end; flex-wrap: wrap; gap: 14px; margin: 18px 0 2px; background: #FFFDF8; border: 1px solid #E6DFCF; border-radius: 10px; padding: 16px 20px; }
  .mbox { min-width: 120px; flex: 1; }
  .mbox .ml { font-size: 10px; letter-spacing: .12em; text-transform: uppercase; color: #6C756D; margin-bottom: 6px; }
  .op { font-size: 20px; font-weight: 700; color: #B58F2D; padding-bottom: 20px; }
  .fill { height: 30px; border: 1.5px solid #0C3D2D; border-radius: 6px; background: #fff; }
  .hint { font-size: 10px; color: #A79B7A; margin-top: 4px; font-style: italic; }
  .pace { font-size: 12.5px; color: #6C756D; margin: 10px 2px 0; }
  h2 { font-size: 14px; font-weight: 700; color: #0C3D2D; text-transform: uppercase; letter-spacing: .05em; margin: 24px 0 8px; display: flex; align-items: center; gap: 10px; }
  h2::before { content: ""; width: 24px; height: 3px; background: #B58F2D; border-radius: 3px; }
  table { width: 100%; border-collapse: collapse; background: #FFFDF8; border: 1px solid #E6DFCF; border-radius: 10px; overflow: hidden; }
  thead th { font-size: 10px; letter-spacing: .1em; text-transform: uppercase; color: #6C756D; text-align: left; background: #F1E8D6; padding: 8px 14px; }
  thead th.you { color: #0C3D2D; }
  td { padding: 9px 14px; font-size: 13.5px; border-top: 1px solid #E6DFCF; vertical-align: middle; }
  td.name { font-weight: 600; width: 34%; }
  td.hintcell { font-size: 11px; font-style: italic; color: #A79B7A; width: 20%; }
  td.fillcell { width: 23%; } td.fillcell::after { content: ""; display: block; height: 22px; border: 1.3px solid #0C3D2D; border-radius: 5px; background: #fff; }
  tr.total td { border-top: 2px solid #B58F2D; font-weight: 700; }
  .ftr { margin-top: 22px; padding-top: 12px; border-top: 1px solid #E6DFCF; display: flex; justify-content: space-between; font-size: 11px; color: #6C756D; } .ftr b { color: #0C3D2D; }
</style></head>
<body><div class="sheet">
  <div class="hdr">
    <div class="wm">TEE BOX</div><div class="eyebrow">Owner Playbook</div>
    <h1>Targets</h1><div class="rule"></div>
    <div class="sub">Set your own targets — our HQ numbers are a starting point. Fill the boxes and track each week.</div>
  </div>
  ${mathHtml}
  <h2>Weekly Lead Target by Channel</h2>
  <table><thead><tr><th>Channel</th><th>HQ suggests</th><th class="you">Your target</th><th class="you">This week</th></tr></thead><tbody>${channelRows}${totalRow}</tbody></table>
  <h2>Cumulative Targets by Phase</h2>
  <table><thead><tr><th>Phase</th><th>HQ milestone</th><th class="you">Your goal</th></tr></thead><tbody>${phaseRows}</tbody></table>
  <div class="ftr"><span><b>Tee Box</b> · HQ Owner Playbook</span><span>Your numbers, your Box. HQ values are a guide.</span></div>
</div>
<script>window.onload=function(){setTimeout(function(){window.print();},250);};</script>
</body></html>`;

  const w = window.open('', '_blank', 'width=900,height=1000');
  if (!w) return;
  w.document.open();
  w.document.write(html);
  w.document.close();
}
